import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuthStore } from '@/store/authStore';
import { useToast } from '@/components/ui/toast';
import { useQueryClient } from '@tanstack/react-query';
import { formatStorage, type BillingEvent } from '@my360/shared';

interface UseBillingSocketOptions {
  onPaymentConfirmed?: (plan: string) => void;
  onPaymentOverdue?: (daysOverdue: number) => void;
  onPaymentFailed?: (reason: string) => void;
  onSubscriptionCancelled?: () => void;
  onPlanUpgraded?: (from: string, to: string) => void;
  onStorageWarning?: (percent: number) => void;
  /** Fired when a storage pack PIX/CC payment confirms — args: packSlug, bonusBytes */
  onStoragePackActivated?: (packSlug: string, bonusBytes: number) => void;
  /** Fired when a pack subscription falls overdue (recurring payment failed) */
  onStoragePackOverdue?: (packSlug: string) => void;
  /** Fired when a pack is cancelled by user OR by Asaas */
  onStoragePackCancelled?: (packSlug: string) => void;
  /** Fired when a pack PIX expires/refunded/deleted (failed before activation) */
  onStoragePackFailed?: (packSlug: string, reason: string) => void;
  enabled?: boolean;
}

interface UseBillingSocketReturn {
  isConnected: boolean;
  lastEvent: BillingEvent | null;
  reconnect: () => void;
}

import { getWsOrigin } from '@/lib/api-base';
// Get WebSocket origin — handles browser (current host) and Capacitor
// (absolute wss://my360.site). See lib/api-base.ts for details.
function getWsUrl(): string {
  return getWsOrigin();
}

export function useBillingSocket(options: UseBillingSocketOptions = {}): UseBillingSocketReturn {
  const { isAuthenticated, checkAuth } = useAuthStore();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { enabled = true } = options;

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const heartbeatIntervalRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const isConnectingRef = useRef(false);
  const isUnmountingRef = useRef(false);
  const hasConnectedOnceRef = useRef(false);

  // Store callbacks in refs to avoid dependency changes
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const [isConnected, setIsConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState<BillingEvent | null>(null);

  const maxReconnectAttempts = 3;
  const baseReconnectDelay = 2000;

  const cleanup = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.onopen = null;
      wsRef.current.onmessage = null;
      wsRef.current.onerror = null;
      wsRef.current.onclose = null;
      if (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING) {
        wsRef.current.close(1000, 'Cleanup');
      }
      wsRef.current = null;
    }
    isConnectingRef.current = false;
  }, []);

  const connect = useCallback(() => {
    // Don't connect if not authenticated, already connecting, unmounting, or already have connection
    if (!isAuthenticated || !enabled || isConnectingRef.current || isUnmountingRef.current) {
      return;
    }

    // Don't reconnect if already connected
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      return;
    }

    // Get token
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    // Close existing connection
    cleanup();

    isConnectingRef.current = true;

    try {
      const wsUrl = `${getWsUrl()}/ws/billing?token=${encodeURIComponent(accessToken)}`;
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        if (isUnmountingRef.current) {
          ws.close();
          return;
        }
        console.log('[WS Billing] Connected');
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
        isConnectingRef.current = false;
        hasConnectedOnceRef.current = true;

        // Setup heartbeat
        heartbeatIntervalRef.current = window.setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'PING' }));
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as BillingEvent;
          setLastEvent(data);

          const currentOptions = optionsRef.current;

          switch (data.type) {
            case 'CONNECTED':
              // Silent - already logged connection
              break;

            case 'PAYMENT_CONFIRMED':
              toast.success(
                'Pagamento confirmado!',
                `Seu plano ${data.plan.toUpperCase()} foi ativado.`
              );
              queryClient.invalidateQueries({ queryKey: ['billing'] });
              queryClient.invalidateQueries({ queryKey: ['projects'] });
              currentOptions.onPaymentConfirmed?.(data.plan);
              checkAuth();
              break;

            case 'PAYMENT_OVERDUE':
              toast.warning(
                'Pagamento atrasado',
                `Seu pagamento está ${data.daysOverdue} dia(s) atrasado.`
              );
              queryClient.invalidateQueries({ queryKey: ['billing'] });
              currentOptions.onPaymentOverdue?.(data.daysOverdue);
              break;

            case 'PAYMENT_FAILED':
              toast.error('Pagamento falhou', data.reason);
              queryClient.invalidateQueries({ queryKey: ['billing'] });
              currentOptions.onPaymentFailed?.(data.reason);
              break;

            case 'SUBSCRIPTION_CANCELLED':
              toast.info('Assinatura cancelada', 'Você foi movido para o plano Free.');
              queryClient.invalidateQueries({ queryKey: ['billing'] });
              currentOptions.onSubscriptionCancelled?.();
              checkAuth();
              break;

            case 'PLAN_UPGRADED':
              toast.success(
                'Upgrade realizado!',
                `Você atualizou de ${data.from.toUpperCase()} para ${data.to.toUpperCase()}.`
              );
              currentOptions.onPlanUpgraded?.(data.from, data.to);
              checkAuth();
              break;

            case 'STORAGE_WARNING':
              if (data.percent >= 95) {
                toast.warning(
                  'Armazenamento quase cheio!',
                  `Você está usando ${data.percent.toFixed(0)}% do seu limite.`
                );
              } else if (data.percent >= 80) {
                toast.info(
                  'Aviso de armazenamento',
                  `Você está usando ${data.percent.toFixed(0)}% do seu limite.`
                );
              }
              currentOptions.onStorageWarning?.(data.percent);
              break;

            // ─── Storage pack lifecycle ─────────────────────────────────────
            case 'STORAGE_PACK_ACTIVATED':
              toast.success(
                'Pack ativado!',
                `+${formatStorage(data.bonusBytes)} liberados na sua conta.`,
              );
              queryClient.invalidateQueries({ queryKey: ['storage-packs', 'my'] });
              queryClient.invalidateQueries({ queryKey: ['billing'] });
              currentOptions.onStoragePackActivated?.(data.packSlug, data.bonusBytes);
              break;

            case 'STORAGE_PACK_OVERDUE':
              toast.warning(
                'Pack em atraso',
                'A renovação do seu pack falhou. Atualize o pagamento para manter o storage extra.',
              );
              queryClient.invalidateQueries({ queryKey: ['storage-packs', 'my'] });
              queryClient.invalidateQueries({ queryKey: ['billing'] });
              currentOptions.onStoragePackOverdue?.(data.packSlug);
              break;

            case 'STORAGE_PACK_CANCELLED':
              toast.info('Pack cancelado', 'O storage extra foi removido da sua conta.');
              queryClient.invalidateQueries({ queryKey: ['storage-packs', 'my'] });
              queryClient.invalidateQueries({ queryKey: ['billing'] });
              currentOptions.onStoragePackCancelled?.(data.packSlug);
              break;

            case 'STORAGE_PACK_FAILED':
              toast.error('Compra do pack falhou', data.reason);
              queryClient.invalidateQueries({ queryKey: ['storage-packs', 'my'] });
              currentOptions.onStoragePackFailed?.(data.packSlug, data.reason);
              break;

            case 'PONG':
              // Heartbeat response - silent
              break;
          }
        } catch (err) {
          console.error('[WS Billing] Failed to parse message:', err);
        }
      };

      ws.onerror = () => {
        // Don't log error details, they're not useful for WebSocket
        // The onclose handler will handle reconnection
        isConnectingRef.current = false;
      };

      ws.onclose = (event) => {
        setIsConnected(false);
        isConnectingRef.current = false;

        // Clean up heartbeat
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }

        // Don't reconnect if unmounting, intentional close, or auth failure
        if (isUnmountingRef.current || event.code === 1000 || event.code === 4001) {
          if (event.code === 4001) {
            console.log('[WS Billing] Authentication failed, not reconnecting');
          }
          return;
        }

        // Only attempt reconnect if we had connected successfully before
        // This prevents reconnection loops on initial connection failures
        if (hasConnectedOnceRef.current && reconnectAttemptsRef.current < maxReconnectAttempts && isAuthenticated && enabled) {
          const delay = baseReconnectDelay * Math.pow(2, reconnectAttemptsRef.current);
          reconnectAttemptsRef.current++;

          console.log(`[WS Billing] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`);

          reconnectTimeoutRef.current = window.setTimeout(() => {
            if (!isUnmountingRef.current) {
              connect();
            }
          }, delay);
        } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          console.log('[WS Billing] Max reconnect attempts reached, giving up');
        }
      };

      wsRef.current = ws;
    } catch (err) {
      console.error('[WS Billing] Failed to create connection:', err);
      isConnectingRef.current = false;
    }
  }, [isAuthenticated, enabled, toast, checkAuth, cleanup, queryClient]);

  const reconnect = useCallback(() => {
    reconnectAttemptsRef.current = 0;
    connect();
  }, [connect]);

  // Store connect in ref to use in effect without causing loops
  const connectRef = useRef(connect);
  connectRef.current = connect;

  // Connect on mount/auth change
  useEffect(() => {
    isUnmountingRef.current = false;
    let timer: number | null = null;

    if (isAuthenticated && enabled) {
      // Small delay to ensure token is available
      timer = window.setTimeout(() => {
        // Only connect if not already connected
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          connectRef.current();
        }
      }, 500);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isAuthenticated, enabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isUnmountingRef.current = true;
      cleanup();
    };
  }, [cleanup]);

  return {
    isConnected,
    lastEvent,
    reconnect,
  };
}
